import React, { useState } from 'react';
import { Building2, CalendarDays, ClipboardList, Settings, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';

const PROFILES = [
  {
    id: 'essential_records',
    title: 'Prontuário clínico',
    description: 'Pacientes, prontuários e anamneses, sem apresentar a gestão completa agora.',
    icon: ClipboardList,
  },
  {
    id: 'care_with_agenda',
    title: 'Atendimento com agenda',
    description: 'Prontuários, anamneses, agenda, lembretes e rotina diária.',
    icon: CalendarDays,
    recommended: true,
  },
  {
    id: 'complete_management',
    title: 'Gestão completa da clínica',
    description: 'Todos os módulos, indicadores e etapas de descoberta desde o início.',
    icon: Building2,
  },
  {
    id: 'manual',
    title: 'Escolher módulos depois',
    description: 'Começar pelo essencial e depois escolher os módulos em Configurações → Menu.',
    icon: SlidersHorizontal,
  },
];

const OnboardingWelcome = () => {
  const onboarding = useOnboarding();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState('care_with_agenda');
  if (!onboarding?.state?.enabled || !onboarding.state.requires_welcome) return null;

  const begin = async () => {
    setSaving(true);
    try {
      await onboarding.start(selectedProfile);
      navigate(
        user?.pending_compliance_assessment_id
          ? '/configuracoes?tab=basicos&continue=compliance'
          : '/configuracoes?tab=basicos'
      );
    } finally { setSaving(false); }
  };

  return <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
    <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700"><Settings className="h-6 w-6" /></span>
      <h2 id="welcome-title" className="mt-4 text-2xl font-bold text-gray-900">Como você pretende usar o OdontoAssist inicialmente?</h2>
      <p className="mt-2 text-sm leading-6 text-gray-600">Mostraremos primeiro apenas o necessário para esse objetivo. Você poderá ativar, ocultar ou descobrir outros recursos quando quiser.</p>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {PROFILES.map((profile) => {
          const Icon = profile.icon;
          const selected = selectedProfile === profile.id;
          return (
            <button
              key={profile.id}
              type="button"
              onClick={() => setSelectedProfile(profile.id)}
              aria-pressed={selected}
              className={`relative rounded-xl border p-4 text-left transition ${selected ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-gray-200 hover:border-indigo-300'}`}
            >
              {profile.recommended && <span className="absolute right-3 top-3 rounded-full bg-indigo-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">Recomendado</span>}
              <Icon className="h-5 w-5 text-indigo-600" />
              <p className="mt-3 pr-20 font-semibold text-gray-900">{profile.title}</p>
              <p className="mt-1 text-sm leading-5 text-gray-600">{profile.description}</p>
            </button>
          );
        })}
      </div>
      <div className="mt-5 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
        {selectedProfile === 'manual'
          ? 'Em seguida, começaremos pelos dados indispensáveis da clínica. Depois, você poderá abrir Configurações → Menu para escolher o que deseja exibir.'
          : 'Em seguida, começaremos pelos dados indispensáveis da clínica. A escolha não remove funcionalidades nem altera permissões.'}
      </div>
      <button type="button" onClick={begin} disabled={saving} className="mt-5 w-full rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">{saving ? 'Preparando...' : 'Continuar para as configurações essenciais'}</button>
    </div>
  </div>;
};

export default OnboardingWelcome;
